import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { formatMoneyDisplay } from "../utils/money";
import { MAX_LEAD_BUYERS } from "./useCredits";
import { sendPushNotification } from "../utils/sendPushNotification";
import {
  buildLeadSubmissionPayload,
  buildSavedLeadRequest,
} from "../utils/leadSubmissionPayload";

// Lightweight initial select — sensitive contact fields (customer_name, customer_phone,
// customer_email, address) are intentionally excluded. Those are only fetched via
// loadLeadDetails() which is called after unlock is confirmed. This prevents locked-lead
// contact data from sitting in the client's JS heap for every marketplace lead.
const LEADS_SELECT = "id, trade, project_type, property_type, suburb, postcode, status, lead_quality_score, scope_summary, condition, urgency, estimate_low, estimate_high, created_at, direct_business_id, job_booked, job_booked_date, archived_at, invoice_sent, invoiced_at, tradie_notes, tradie_notes_photos, lead_unlocks(count), lead_media(count)";

// Merge marketplace previews with the tradie's accessible leads.
// Accessible-table rows take precedence: they reflect the live status after
// quote/accept/dismiss, whereas the marketplace view filters to status='open'
// and would otherwise drop these leads from the pipeline entirely.
function mergeLeadSources(marketplaceRows, accessibleRows) {
  const byId = new Map();
  for (const row of marketplaceRows || []) byId.set(row.id, row);
  for (const row of accessibleRows || []) byId.set(row.id, row);
  return Array.from(byId.values());
}

function normalizeRawFormJson(details) {
  const raw = details?.raw_form_json;
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
}

function mapLeadRowsToRequests(leads, quotes, businessId) {
  return (leads || []).map((lead) => {
    // PostgREST returns counts as [{ count: N }] — Number() handles string/number
    const soldCount = Number(lead.lead_unlocks?.[0]?.count ?? 0);
    const remainingSlots = Math.max(0, MAX_LEAD_BUYERS - soldCount);
    const soldOut = soldCount >= MAX_LEAD_BUYERS;
    const photoCount = Number(lead.lead_media?.[0]?.count ?? 0);

    // Match this business's quote from the separately-fetched quotes array
    const businessQuote = businessId
      ? (quotes || []).find((q) => q.lead_id === lead.id) || null
      : null;

    return {
      id: lead.id,
      // customer_name is not in LEADS_SELECT — null until loadLeadDetails() after unlock
      customer: null,
      suburb: lead.suburb || "Unknown suburb",
      postcode: lead.postcode || "",
      trade: lead.trade,
      type: lead.project_type,
      status: lead.status || "open",
      score: lead.lead_quality_score || 0,
      areas: lead.scope_summary || "",
      complexity: lead.condition || "",
      budget:
        lead.estimate_low && lead.estimate_high
          ? `${formatMoneyDisplay(lead.estimate_low)} – ${formatMoneyDisplay(lead.estimate_high)}`
          : "",
      photos: photoCount,
      soldCount,
      remainingSlots,
      soldOut,
      lastUpdated: "Loaded from Supabase",
      // Signals whether lead_details + lead_media rows have been fetched.
      // Set to true by loadLeadDetails; prevents duplicate fetches.
      detailsLoaded: false,
      form: {
        // Contact fields are intentionally empty until loadLeadDetails() is called
        // for an unlocked lead — they must not come from the marketplace query.
        fullName: "",
        phone: "",
        email: "",
        address: "",
        suburb: lead.suburb || "",
        postcode: lead.postcode || "",
        trade: lead.trade || "Painting",
        projectType: lead.project_type || "",
        propertyType: lead.property_type || "",
        areas: lead.scope_summary || "",
        condition: lead.condition || "",
        urgency: lead.urgency || "Standard",
        surfaces: [],
        // Detail fields start empty; populated by loadLeadDetails
        measurements: "",
        notes: "",
        accessDetails: "",
        serviceCategory: "",
        issueType: "",
        quoteFor: [],
        progressPayments: false,
        cashPayment: false,
        customerRecordOnly: false,
        uploadedPhotos: [],
      },
      quote: businessQuote
        ? {
            id: businessQuote.id,
            quote_number: businessQuote.quote_number || null,
            public_token: businessQuote.public_token,
            status: businessQuote.status || "sent",
            subtotal: Number(businessQuote.subtotal) || 0,
            gst: Number(businessQuote.gst) || 0,
            total: Number(businessQuote.total) || 0,
            price: businessQuote.total
              ? formatMoneyDisplay(businessQuote.total)
              : "",
            notes: businessQuote.intro_text || "",
            sentAt:
              String(businessQuote.status || "").toLowerCase() === "draft"
                ? null
                : businessQuote.sent_at || businessQuote.created_at,
            line_items: businessQuote.line_items || [],
            business_id: businessQuote.business_id || null,
            customer_status: businessQuote.customer_status || "pending",
            deposit_paid: businessQuote.deposit_paid || false,
            deposit_amount: businessQuote.deposit_amount || null,
            deposit_paid_at: businessQuote.deposit_paid_at || null,
            view_count: businessQuote.view_count || 0,
            last_viewed_at: businessQuote.last_viewed_at || businessQuote.viewed_at || null,
            viewed_at: businessQuote.viewed_at || null,
            accepted_at: businessQuote.accepted_at || null,
          }
        : null,
      directBusinessId: lead.direct_business_id || null,
      createdAt: lead.created_at,
      jobBooked: lead.job_booked || false,
      jobBookedDate: lead.job_booked_date || null,
      tradieNotes: lead.tradie_notes || "",
      tradieNotesPhotos: Array.isArray(lead.tradie_notes_photos) ? lead.tradie_notes_photos : [],
      // Emit both casings so legacy code reading lead.archived_at AND new
      // code reading lead.archivedAt both work. Without this, the kanban
      // filter (lead.archived_at) loses the archive bit after a reload
      // because mapLeadRowsToRequests only emitted camelCase. See archive bug.
      archivedAt: lead.archived_at || null,
      archived_at: lead.archived_at || null,
      invoicedAt: lead.invoiced_at || null,
      invoiced_at: lead.invoiced_at || null,
      invoiceSent: !!lead.invoice_sent,
      invoice_sent: !!lead.invoice_sent,
    };
  });
}

const ALLOWED_UPLOAD_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "mp4", "mov", "avi", "mkv", "webm", "m4v", "3gp"];

// Maps allowed extensions to safe content types so we never trust the client-supplied type.
const SAFE_CONTENT_TYPES = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  webm: "video/webm",
  m4v: "video/x-m4v",
  "3gp": "video/3gpp",
};

async function uploadLeadPhotosToSupabase(leadId, uploadedPhotos) {
  const uploadedMediaRows = [];

  for (const photo of uploadedPhotos) {
    const fileExt = (photo.name.split(".").pop() || "").toLowerCase();

    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(fileExt)) {
      throw new Error(`File type .${fileExt} is not allowed. Please upload an image or video (jpg, png, mp4, mov, etc.).`);
    }

    if (photo.size && photo.size > 50 * 1024 * 1024) {
      throw new Error(`File "${photo.name}" is too large (${(photo.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 50 MB.`);
    }

    const safeContentType = SAFE_CONTENT_TYPES[fileExt] || "image/jpeg";
    const filePath = `${leadId}/${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}.${fileExt}`;

    const response = await fetch(photo.dataUrl);
    if (!response.ok) throw new Error(`Failed to read photo "${photo.name}"`);
    const blob = await response.blob();

    const { error: uploadError } = await supabase.storage
      .from("lead-photos")
      .upload(filePath, blob, {
        contentType: safeContentType,
        upsert: false,
      });

    if (uploadError) throw uploadError;

    const { data: publicUrlData } = supabase.storage
      .from("lead-photos")
      .getPublicUrl(filePath);

    uploadedMediaRows.push({
      lead_id: leadId,
      file_url: publicUrlData.publicUrl,
      file_name: photo.name,
      file_type: photo.type,
      file_size: photo.size,
    });
  }

  return uploadedMediaRows;
}

// SECURITY: businessId must come from useAuth() session, never from URL params or user input.
// Verified in useAuth.js — business_id is read from profiles using session.user.id as the filter.
export function useLeads({ session, businessId, showToast = () => {} }) {
  const [requests, setRequests] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  // Per-tradie dismissed leads — Set of lead UUIDs. Backed by the
  // lead_dismissals table so dismissals survive a refresh.
  const [dismissedLeadIds, setDismissedLeadIds] = useState(() => new Set());

  useEffect(() => {
    if (!session) return;

    let cancelled = false;

    async function load() {
      setLeadsLoading(true);
      const [marketplaceResult, accessibleResult, quotesResult, dismissalsResult] = await Promise.all([
        // Pre-unlock marketplace previews — typically a SECURITY DEFINER view
        // that exposes status='open' leads to all tradies with PII masked. We
        // can't put unlocked/quoted/accepted leads here because the view
        // filters them out — that's why we also fetch from `leads` below.
        supabase
          .from("leads_marketplace")
          .select(LEADS_SELECT)
          .order("created_at", { ascending: false }),
        // Pipeline source — RLS scopes this to the leads this tradie has
        // actually been granted access to (unlocked / direct / winner). Status
        // changes after unlock (Quoted, Accepted, dismissed) keep the row in
        // this result, which is what makes the pipeline tabs stable.
        businessId
          ? supabase
              .from("leads")
              .select(LEADS_SELECT)
              .order("created_at", { ascending: false })
          : Promise.resolve({ data: [], error: null }),
        businessId
          ? supabase
              .from("quotes")
              // Enumerate only needed columns — avoids pulling large line_items JSON for every quote on load
              .select("id, lead_id, business_id, total, subtotal, gst, gst_registered, intro_text, line_items, customer_status, status, created_at, updated_at, public_token, deposit_paid, deposit_amount, deposit_paid_at, sent_at, quote_number, view_count, last_viewed_at, viewed_at, accepted_at")
              .eq("business_id", businessId)
          : Promise.resolve({ data: [], error: null }),
        businessId
          ? supabase
              .from("lead_dismissals")
              .select("lead_id")
              .eq("business_id", businessId)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (cancelled) return;

      // Resilience: if either source fails, keep going with whichever one
      // succeeded. A single 400/503 on one query must NOT blank the entire
      // pipeline — that hid Peter's accepted lead from Shannon when the
      // leads_marketplace view was missing archived_at after the mini-CRM
      // migration.
      if (marketplaceResult.error) {
        showToast(
          `Marketplace query failed: ${marketplaceResult.error.message}`,
          "error"
        );
      }
      if (accessibleResult.error) {
        showToast(
          `Pipeline query failed: ${accessibleResult.error.message}`,
          "error"
        );
      }

      if (!cancelled) {
        setRequests(
          mapLeadRowsToRequests(
            mergeLeadSources(
              marketplaceResult.error ? [] : marketplaceResult.data,
              accessibleResult.error ? [] : accessibleResult.data
            ),
            quotesResult.data || [],
            businessId
          )
        );
        setDismissedLeadIds(
          new Set((dismissalsResult.data || []).map((row) => row.lead_id))
        );
        setLeadsLoading(false);
      }
    }

    load();

    return () => { cancelled = true; };
  }, [session, businessId, showToast]);

  // Exported for external callers (e.g. CustomerQuoteView after a quote response).
  const refreshMainAppData = useCallback(
    async (activeBusinessId = businessId) => {
      if (!session) return;

      const [marketplaceResult, accessibleResult, quotesResult] = await Promise.all([
        supabase
          .from("leads_marketplace")
          .select(LEADS_SELECT)
          .order("created_at", { ascending: false }),
        activeBusinessId
          ? supabase
              .from("leads")
              .select(LEADS_SELECT)
              .order("created_at", { ascending: false })
          : Promise.resolve({ data: [], error: null }),
        activeBusinessId
          ? supabase
              .from("quotes")
              .select("id, lead_id, business_id, total, subtotal, gst, gst_registered, intro_text, line_items, customer_status, status, created_at, updated_at, public_token, deposit_paid, deposit_amount, deposit_paid_at, sent_at, quote_number, view_count, last_viewed_at, viewed_at, accepted_at")
              .eq("business_id", activeBusinessId)
          : Promise.resolve({ data: [], error: null }),
      ]);

      // Same resilience as the initial load — never blank the pipeline
      // because one source 400/503'd.
      if (marketplaceResult.error) {
        showToast(
          `Marketplace query failed: ${marketplaceResult.error.message}`,
          "error"
        );
      }
      if (accessibleResult.error) {
        showToast(
          `Pipeline query failed: ${accessibleResult.error.message}`,
          "error"
        );
      }

      setRequests(
        mapLeadRowsToRequests(
          mergeLeadSources(
            marketplaceResult.error ? [] : marketplaceResult.data,
            accessibleResult.error ? [] : accessibleResult.data
          ),
          quotesResult.data || [],
          activeBusinessId
        )
      );
    },
    [session, businessId, showToast]
  );

  // Fetches contact details + lead_details + lead_media for a single lead on demand.
  // ONLY called after a tradie has unlocked (or is direct/winner on) this lead.
  // This is the sole path through which customer_name, customer_phone, customer_email,
  // and address are loaded — they are intentionally absent from LEADS_SELECT.
  // RLS on `leads` enforces the access gate server-side as a second layer.
  const loadLeadDetails = useCallback(async (leadId) => {
    const [leadResult, detailsResult, mediaResult] = await Promise.all([
      supabase
        .from("leads")
        .select(
          "customer_name, customer_phone, customer_email, address, suburb, postcode"
        )
        .eq("id", leadId)
        .maybeSingle(),
      supabase
        .from("lead_details")
        .select("id, lead_id, areas, measurements, access_details, issue_type, service_category, notes, raw_form_json, created_at")
        .eq("lead_id", leadId)
        .maybeSingle(),
      supabase.from("lead_media").select("*").eq("lead_id", leadId),
    ]);

    const leadRow = leadResult.data || {};
    const details = detailsResult.data || {};
    const rawForm = normalizeRawFormJson(details);
    const mediaRows = mediaResult.data || [];

    setRequests((prev) =>
      prev.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              // Refresh header-level fields so the lead card title and
              // summary reflect the real customer once unlocked.
              customer: leadRow.customer_name || lead.customer || "Customer",
              suburb: leadRow.suburb || lead.suburb,
              photos: mediaRows.length,
              detailsLoaded: true,
              form: {
                ...lead.form,
                ...rawForm,
                // Unmasked contact info — the primary fix for the
                // "unknown customer" bug on the tradie dashboard.
                fullName: leadRow.customer_name || rawForm.fullName || "",
                phone: leadRow.customer_phone || rawForm.phone || lead.form.phone,
                email: leadRow.customer_email || rawForm.email || lead.form.email,
                address: leadRow.address || rawForm.address || lead.form.address,
                areas: details.areas || rawForm.areas || lead.areas || "",
                measurements: details.measurements || rawForm.measurements || "",
                notes: details.notes || rawForm.notes || "",
                accessDetails: details.access_details || rawForm.accessDetails || "",
                serviceCategory: details.service_category || rawForm.serviceCategory || "",
                issueType: details.issue_type || rawForm.issueType || "",
                uploadedPhotos: mediaRows.map((media) => ({
                  name: media.file_name || "image",
                  type: media.file_type || "image/jpeg",
                  size: media.file_size || 0,
                  dataUrl: media.file_url,
                })),
              },
            }
          : lead
      )
    );
  }, []);

  const autoLoadRan = React.useRef(false);

  // Auto-fetch details for all unlocked leads so customer names appear
  // in the lead list without needing to click each card individually.
  useEffect(() => {
    if (!session || !businessId) { autoLoadRan.current = false; return; }
    if (autoLoadRan.current) return;

    async function loadUnlockedDetails() {
      const { data: unlockData } = await supabase
        .from("lead_unlocks")
        .select("lead_id")
        .eq("business_id", businessId);

      const unlockedIds = (unlockData || []).map((row) => row.lead_id);

      const directIds = requests
        .filter((r) => r.directBusinessId === businessId)
        .map((r) => r.id);

      const allIds = [...new Set([...unlockedIds, ...directIds])];
      if (allIds.length === 0) return;

      autoLoadRan.current = true;

      // Batch fetch all lead details in parallel instead of one at a time
      const needsLoad = allIds.filter((id) => {
        const existing = requests.find((r) => r.id === id);
        return existing && !existing.detailsLoaded;
      });

      if (needsLoad.length === 0) return;

      // Fetch leads contact info - this uses leads_select_business_access RLS
      const leadsResult = await supabase
        .from("leads")
        .select("id, customer_name, customer_phone, customer_email, address, suburb, postcode")
        .in("id", needsLoad);

      // Fetch lead_details individually to avoid RLS function 500 on batch .in()
      const detailsResults = await Promise.all(
        needsLoad.map((id) =>
          supabase.from("lead_details").select("id, lead_id, areas, measurements, access_details, issue_type, service_category, notes, raw_form_json, created_at").eq("lead_id", id).maybeSingle()
            .then(({ data }) => data)
            .catch(() => null)
        )
      );
      const detailsResult = { data: detailsResults.filter(Boolean) };

      // Fetch media - uses same RLS function but media rows are less critical
      let mediaResult = { data: [] };
      try {
        const res = await supabase.from("lead_media").select("*").in("lead_id", needsLoad);
        mediaResult = res;
      } catch {
        // Non-fatal — media will load when lead is selected individually
      }

      const leadsMap = {};
      (leadsResult.data || []).forEach((l) => { leadsMap[l.id] = l; });

      const detailsMap = {};
      (detailsResult.data || []).forEach((d) => { detailsMap[d.lead_id] = d; });

      const mediaMap = {};
      (mediaResult.data || []).forEach((m) => {
        if (!mediaMap[m.lead_id]) mediaMap[m.lead_id] = [];
        mediaMap[m.lead_id].push(m);
      });

      // Update all leads in one setRequests call. Always flip detailsLoaded
      // (even when the row isn't in leadsMap) so the kanban card stops
      // showing "Loading..." indefinitely on a silent fetch failure —
      // the fallback chain in PipelineCard handles a null customer fine.
      setRequests((prev) =>
        prev.map((lead) => {
          if (!needsLoad.includes(lead.id)) return lead;
          const leadRow = leadsMap[lead.id] || {};
          const details = detailsMap[lead.id] || {};
          const rawForm = normalizeRawFormJson(details);
          const mediaRows = mediaMap[lead.id] || [];

          return {
            ...lead,
            customer: leadRow.customer_name || lead.customer || null,
            suburb: leadRow.suburb || lead.suburb,
            postcode: leadRow.postcode || lead.postcode,
            photos: mediaRows.length,
            detailsLoaded: true,
            form: {
              ...lead.form,
              ...rawForm,
              fullName: leadRow.customer_name || rawForm.fullName || "",
              phone: leadRow.customer_phone || rawForm.phone || lead.form.phone,
              email: leadRow.customer_email || rawForm.email || lead.form.email,
              address: leadRow.address || rawForm.address || lead.form.address,
              areas: details.areas || rawForm.areas || lead.areas || "",
              measurements: details.measurements || rawForm.measurements || "",
              notes: details.notes || rawForm.notes || "",
              accessDetails: details.access_details || rawForm.accessDetails || "",
              serviceCategory: details.service_category || rawForm.serviceCategory || "",
              issueType: details.issue_type || rawForm.issueType || "",
              uploadedPhotos: mediaRows.map((media) => ({
                name: media.file_name || "image",
                type: media.file_type || "image/jpeg",
                size: media.file_size || 0,
                dataUrl: media.file_url,
              })),
            },
          };
        })
      );
    }

    // Only run after initial leads have loaded
    if (!leadsLoading && requests.length > 0) {
      loadUnlockedDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, businessId, leadsLoading, loadLeadDetails]);

  // Returns the saved request on success, or null on failure
  async function submitLead(
    request,
    customerId = null,
    directBusinessId = null,
    options = {}
  ) {
    // Accept tradieId from either the explicit arg (App.js path) or
    // embedded in the request object (LeadForm prop path) — whichever is set.
    const resolvedDirectBusinessId =
      directBusinessId || request.directBusinessId || null;

    const leadId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : null;
    if (!leadId) {
      const error = new Error("Could not prepare lead submission.");
      showToast(error.message, "error");
      return { ok: false, error };
    }

    let mediaRows = [];
    if (request.form.uploadedPhotos && request.form.uploadedPhotos.length > 0) {
      try {
        mediaRows = await uploadLeadPhotosToSupabase(
          leadId,
          request.form.uploadedPhotos
        );
      } catch (mediaUploadError) {
        showToast(`Photo upload failed: ${mediaUploadError.message}`, "error");
        return { ok: false, error: mediaUploadError };
      }
    }

    const payload = buildLeadSubmissionPayload({
      request,
      leadId,
      customerId,
      directBusinessId: resolvedDirectBusinessId,
      mediaRows,
    });

    const { data, error } = await supabase.rpc("submit_lead_atomic", {
      p_lead: payload.lead,
      p_details: payload.details,
      p_media: payload.media,
    });

    if (error) {
      showToast(`Failed to save lead: ${error.message}`, "error");
      return { ok: false, error };
    }

    const savedRequest = buildSavedLeadRequest({
      request,
      leadId: data || leadId,
      maxLeadBuyers: MAX_LEAD_BUYERS,
    });

    // Notify the tradie when a customer submits a direct lead to their business
    if (resolvedDirectBusinessId && options.notifyDirectBusiness !== false) {
      // (1) Push notification — fast path, fire-and-forget.
      supabase
        .from("profiles")
        .select("id")
        .eq("business_id", resolvedDirectBusinessId)
        .maybeSingle()
        .then(({ data: profileData }) => {
          if (profileData?.id) {
            sendPushNotification(profileData.id, {
              title: "New job request 🔔",
              body: `${request.trade} job in ${request.suburb} — tap to review`,
              url: "/",
            });
          }
        });

      // (2) Email — the customer page is unauthenticated, so we hit a
      // service-role edge function that resolves the tradie's email + sends.
      // Best-effort; failure is non-fatal (push already fired).
      try {
        const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-new-lead-email`;
        fetch(functionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            businessId: resolvedDirectBusinessId,
            customerName: request.customer || request.form?.fullName || "",
            customerPhone: request.form?.phone || "",
            customerEmail: request.form?.email || "",
            trade: request.trade || "",
            projectType: request.type || request.form?.projectType || "",
            suburb: request.suburb || "",
            whenNeeded: request.form?.whenNeeded || "",
            notes: request.form?.notes || request.form?.tradeSpecificAnswers?.handyNotes || "",
            // Include leadId so the edge function can deep-link the
            // "Open in pipeline" CTA directly to this lead's detail panel.
            leadId,
            appUrl: typeof window !== "undefined" ? window.location.origin : "",
          }),
        }).catch(() => { /* non-fatal */ });
      } catch {
        // non-fatal
      }
    }

    setRequests((prev) => [savedRequest, ...prev]);
    return { ok: true, request: savedRequest };
  }

  async function createOnsiteQuoteLead(draft = {}) {
    if (!businessId) {
      showToast("No business is loaded.", "error");
      return { ok: false, reason: "no_business" };
    }

    const customerName = (draft.customer || draft.fullName || "").trim();
    const trade = (draft.trade || "Painting").trim() || "Painting";
    const quoteFor = Array.isArray(draft.quoteFor)
      ? draft.quoteFor.filter(Boolean)
      : (draft.quoteFor ? [draft.quoteFor] : []);
    const customerRecordOnly = draft.customerRecordOnly === true;
    const fallbackProjectType = customerRecordOnly
      ? "Customer record"
      : (quoteFor.length ? quoteFor.join(" and ") : "On-site quote");
    const projectType = (draft.projectType || fallbackProjectType).trim() || fallbackProjectType;
    const suburb = (draft.suburb || "On site").trim() || "On site";
    const postcode = (draft.postcode || "").trim();
    const progressPayments = draft.progressPayments === true;
    const cashPayment = draft.cashPayment === true;
    const scopeNotes = [
      quoteFor.length ? `Quote for: ${quoteFor.join(", ")}` : "",
      progressPayments ? "Progress payments required." : "",
      cashPayment ? "Cash payment option selected. No GST invoice required." : "",
    ].filter(Boolean);
    const notes = [
      scopeNotes.join("\n"),
      (draft.notes || "").trim(),
    ].filter(Boolean).join("\n\n");

    const request = {
      customer: customerName || "On-site customer",
      suburb,
      postcode,
      trade,
      type: projectType,
      areas: projectType,
      complexity: "",
      score: 0,
      estimate: { low: null, high: null },
      directBusinessId: businessId,
      form: {
        fullName: customerName || "On-site customer",
        phone: (draft.phone || "").trim(),
        email: (draft.email || "").trim(),
        address: (draft.address || "").trim(),
        suburb,
        postcode,
        trade,
        projectType,
        quoteFor,
        progressPayments,
        cashPayment,
        customerRecordOnly,
        propertyType: "",
        areas: projectType,
        measurements: "",
        notes,
        accessDetails: "",
        serviceCategory: trade,
        issueType: projectType,
        urgency: "Standard",
        uploadedPhotos: [],
      },
    };

    return submitLead(request, null, businessId, {
      notifyDirectBusiness: false,
    });
  }

  async function updateRequestStatus(id, status) {
    const { error } = await supabase
      .from("leads")
      .update({ status })
      .eq("id", id);

    if (error) {
      showToast(`Failed to update status: ${error.message}`, "error");
      return;
    }

    setRequests((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, status, lastUpdated: "Updated just now" }
          : item
      )
    );
  }

  /**
   * Persist a quote (insert or update) for the given lead and return
   * the freshly-saved row so callers can read public_token / id without
   * waiting for React state to propagate.
   *
   * @param {string} id - leads.id
   * @param {{subtotal:number, gst:number, total:number, lineItems:import("../types").LineItem[], notes:string, gstRegistered:boolean, sentAt?:string, status?:string}} quote
   * @returns {Promise<import("../types").Quote|undefined>}
   */
  async function saveQuote(id, quote) {
    if (!businessId) {
      showToast("No business is loaded.", "error");
      return;
    }

    const existingRequest = requests.find((item) => item.id === id);

    if (!existingRequest) {
      showToast("Could not find request.", "error");
      return;
    }

    const requestedStatus = quote.status === "draft" ? "draft" : "sent";
    const isDraft = requestedStatus === "draft";
    const quotePayload = {
      lead_id: id,
      business_id: businessId,
      status: requestedStatus,
      title: `${existingRequest.trade} quote`,
      intro_text: quote.notes || "",
      subtotal: quote.subtotal || 0,
      gst: quote.gst || 0,
      total: quote.total || 0,
      line_items: quote.lineItems || [],
      // Snapshot the tradie's GST status at send time so toggling later
      // doesn't retroactively change how historical quotes display.
      gst_registered: quote.gstRegistered !== false,
      updated_at: new Date().toISOString(),
    };

    const { data: existingQuotes, error: findError } = await supabase
      .from("quotes")
      .select("*")
      .eq("lead_id", id)
      .eq("business_id", businessId)
      .limit(1);

    if (findError) {
      showToast(`Failed to check existing quote: ${findError.message}`, "error");
      return;
    }

    let savedQuoteRow = null;

    if (existingQuotes && existingQuotes.length > 0) {
      const existingQuote = existingQuotes[0];
      const wasDraft =
        String(existingQuote.status || "").toLowerCase() === "draft" ||
        !existingQuote.sent_at;
      const updatePayload =
        !isDraft && wasDraft
          ? { ...quotePayload, sent_at: quote.sentAt || new Date().toISOString() }
          : quotePayload;
      const { data, error } = await supabase
        .from("quotes")
        .update(updatePayload)
        .eq("id", existingQuote.id)
        .select()
        .single();

      if (error) {
        showToast(`Failed to update quote: ${error.message}`, "error");
        return;
      }

      savedQuoteRow = data;
    } else {
      // First-time send — stamp sent_at so the "Quotes sent this month" stat
      // has a reliable timestamp. We don't overwrite it on subsequent updates.
      const insertPayload = isDraft
        ? quotePayload
        : { ...quotePayload, sent_at: quote.sentAt || new Date().toISOString() };
      const { data, error } = await supabase
        .from("quotes")
        .insert([insertPayload])
        .select()
        .single();

      if (error) {
        showToast(`Failed to save quote: ${error.message}`, "error");
        return;
      }

      savedQuoteRow = data;
    }

    // Auto-advance status to Quoted only when a quote is sent.
    const currentStatus = (requests.find((r) => r.id === id)?.status || "").toLowerCase();
    if (!isDraft && ["open", "new", "needs review", "needs_review", "new request", ""].includes(currentStatus)) {
      await supabase.from("leads").update({ status: "Quoted" }).eq("id", id);
    }

    setRequests((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quote: {
                id: savedQuoteRow?.id,
                quote_number: savedQuoteRow?.quote_number || null,
                public_token: savedQuoteRow?.public_token,
                status: savedQuoteRow?.status || requestedStatus,
                subtotal: Number(savedQuoteRow?.subtotal) || 0,
                gst: Number(savedQuoteRow?.gst) || 0,
                total: Number(savedQuoteRow?.total) || 0,
                price: savedQuoteRow?.total
                  ? formatMoneyDisplay(savedQuoteRow.total)
                  : "$0.00",
                notes: savedQuoteRow?.intro_text || "",
                sentAt:
                  savedQuoteRow?.sent_at ||
                  (isDraft ? null : savedQuoteRow?.created_at || new Date().toISOString()),
                line_items: savedQuoteRow?.line_items || [],
                business_id: savedQuoteRow?.business_id || null,
                customer_status: savedQuoteRow?.customer_status || "pending",
                view_count: savedQuoteRow?.view_count || 0,
                last_viewed_at: savedQuoteRow?.last_viewed_at || savedQuoteRow?.viewed_at || null,
                viewed_at: savedQuoteRow?.viewed_at || null,
              },
              status: isDraft ? item.status : "Quoted",
              lastUpdated: "Saved to Supabase",
            }
          : item
      )
    );

    showToast(isDraft ? "Draft quote saved." : "Quote saved.", "success");
    return savedQuoteRow;
  }

  function handleLeadUnlocked(leadId) {
    setRequests((prev) =>
      prev.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              soldCount: (lead.soldCount || 0) + 1,
              remainingSlots: Math.max(
                0,
                MAX_LEAD_BUYERS - ((lead.soldCount || 0) + 1)
              ),
              soldOut: (lead.soldCount || 0) + 1 >= MAX_LEAD_BUYERS,
            }
          : lead
      )
    );
  }

  async function bookJob(leadId, jobBookedDate) {
    if (!businessId) return { ok: false, reason: "not_ready" };
    const { error } = await supabase
      .from("leads")
      .update({ job_booked: true, job_booked_date: jobBookedDate })
      .eq("id", leadId);

    if (error) {
      showToast(`Failed to book job: ${error.message}`, "error");
      return;
    }

    // Record job completion so subscription tier limits (isJobLimitReached) are enforced correctly.
    try {
      await supabase
        .from("job_completions")
        .insert({ business_id: businessId, lead_id: leadId });
    } catch {
      // Non-fatal — job booking already succeeded; tier counting will self-correct on next load.
    }

    // Auto-advance status to Accepted when a job is booked
    const currentJobStatus = (requests.find((r) => r.id === leadId)?.status || "").toLowerCase();
    if (currentJobStatus !== "accepted") {
      await supabase.from("leads").update({ status: "Accepted" }).eq("id", leadId);
    }

    setRequests((prev) =>
      prev.map((lead) =>
        lead.id === leadId
          ? { ...lead, jobBooked: true, jobBookedDate, status: "Accepted" }
          : lead
      )
    );

    showToast("Job booked!", "success");
  }

  async function undismissLead(leadId) {
    if (!businessId) return;
    // Optimistic — drop locally first, then remove the row. If the delete
    // fails we restore the dismissal so the UI stays honest.
    setDismissedLeadIds((prev) => {
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
    const { error } = await supabase
      .from("lead_dismissals")
      .delete()
      .eq("business_id", businessId)
      .eq("lead_id", leadId);
    if (error) {
      setDismissedLeadIds((prev) => {
        const next = new Set(prev);
        next.add(leadId);
        return next;
      });
      showToast(`Failed to reopen lead: ${error.message}`, "error");
    }
  }

  // ── Mini-CRM stage mutations (Session 1) ────────────────────────────────
  // Move a lead between pipeline stages. Optimistic update + Supabase
  // round-trip + rollback on error. Allowed status values are gated by the
  // leads_status_minicrm_check constraint applied 2026-04-29.
  async function updateLeadStage(leadId, newStatus) {
    if (!leadId) return { ok: false, reason: "no_lead_id" };
    const prev = requests.find((r) => r.id === leadId);
    setRequests((rs) =>
      rs.map((r) => (r.id === leadId ? { ...r, status: newStatus } : r))
    );
    const { error } = await supabase
      .from("leads")
      .update({ status: newStatus })
      .eq("id", leadId);
    if (error) {
      // Rollback the optimistic write.
      if (prev) {
        setRequests((rs) =>
          rs.map((r) => (r.id === leadId ? { ...r, status: prev.status } : r))
        );
      }
      showToast(`Couldn't update stage: ${error.message}`, "error");
      return { ok: false, error };
    }
    return { ok: true };
  }

  // Manually archive a lead — sets archived_at = now(). Pipeline view filters
  // archived leads out of the main board; archive view surfaces them.
  // Emit BOTH archived_at and archivedAt on the optimistic write so the
  // kanban filter (which reads snake_case) and any other consumer reading
  // camelCase stay consistent with what mapLeadRowsToRequests would emit
  // after a reload. Without both fields, refreshing the page after archive
  // makes the card reappear in the active board.
  async function archiveLead(leadId) {
    if (!leadId) return { ok: false, reason: "no_lead_id" };
    const now = new Date().toISOString();
    setRequests((rs) =>
      rs.map((r) =>
        r.id === leadId ? { ...r, archived_at: now, archivedAt: now } : r
      )
    );
    const { error } = await supabase
      .from("leads")
      .update({ archived_at: now })
      .eq("id", leadId);
    if (error) {
      setRequests((rs) =>
        rs.map((r) =>
          r.id === leadId
            ? { ...r, archived_at: null, archivedAt: null }
            : r
        )
      );
      showToast(`Couldn't archive: ${error.message}`, "error");
      return { ok: false, error };
    }
    return { ok: true };
  }

  async function unarchiveLead(leadId) {
    if (!leadId) return { ok: false, reason: "no_lead_id" };
    setRequests((rs) =>
      rs.map((r) =>
        r.id === leadId ? { ...r, archived_at: null, archivedAt: null } : r
      )
    );
    const { error } = await supabase
      .from("leads")
      .update({ archived_at: null })
      .eq("id", leadId);
    if (error) {
      showToast(`Couldn't unarchive: ${error.message}`, "error");
      return { ok: false, error };
    }
    return { ok: true };
  }

  // Edit customer contact + tradie-internal notes from the mini-CRM "Edit
  // details" modal. Single source of truth: the leads table — quote PDFs,
  // invoices, and customer emails all read from these columns, so the edit
  // propagates automatically. Optimistic local update with rollback on error.
  async function updateLeadContactDetails(leadId, fields) {
    if (!leadId) return { ok: false, reason: "no_lead_id" };
    if (!fields || typeof fields !== "object") {
      return { ok: false, reason: "no_fields" };
    }
    // Whitelist the columns we allow to change here. Anything else is ignored.
    const allowed = [
      "customer_name",
      "customer_phone",
      "customer_email",
      "address",
      "suburb",
      "postcode",
      "tradie_notes",
      "tradie_notes_photos",
    ];
    const payload = {};
    for (const k of allowed) {
      if (Object.prototype.hasOwnProperty.call(fields, k)) {
        payload[k] = fields[k];
      }
    }
    if (Object.keys(payload).length === 0) {
      return { ok: false, reason: "empty_payload" };
    }

    // Snapshot for rollback.
    const prev = requests.find((r) => r.id === leadId);

    // Optimistic local update — flatten the leads-table fields onto the
    // existing request shape (top-level + nested form mirror).
    setRequests((rs) =>
      rs.map((r) => {
        if (r.id !== leadId) return r;
        const next = { ...r, form: { ...r.form } };
        if ("customer_name" in payload) {
          next.customer = payload.customer_name || null;
          next.form.fullName = payload.customer_name || "";
        }
        if ("customer_phone" in payload) next.form.phone = payload.customer_phone || "";
        if ("customer_email" in payload) next.form.email = payload.customer_email || "";
        if ("address" in payload) next.form.address = payload.address || "";
        if ("suburb" in payload) {
          next.suburb = payload.suburb || next.suburb;
          next.form.suburb = payload.suburb || "";
        }
        if ("postcode" in payload) {
          next.postcode = payload.postcode || next.postcode;
          next.form.postcode = payload.postcode || "";
        }
        if ("tradie_notes" in payload) next.tradieNotes = payload.tradie_notes || "";
        if ("tradie_notes_photos" in payload) {
          next.tradieNotesPhotos = Array.isArray(payload.tradie_notes_photos)
            ? payload.tradie_notes_photos
            : [];
        }
        next.lastUpdated = "Updated just now";
        return next;
      })
    );

    const { error } = await supabase
      .from("leads")
      .update(payload)
      .eq("id", leadId);

    if (error) {
      // Rollback.
      if (prev) {
        setRequests((rs) => rs.map((r) => (r.id === leadId ? prev : r)));
      }
      showToast(`Couldn't save details: ${error.message}`, "error");
      return { ok: false, error };
    }
    showToast("Customer details saved.", "success");
    return { ok: true };
  }

  async function dismissLead(leadId) {
    if (!businessId) return;
    // Optimistic — flip the local set first so the card disappears
    // immediately, then persist. Undo restores by deleting the row.
    setDismissedLeadIds((prev) => {
      const next = new Set(prev);
      next.add(leadId);
      return next;
    });
    const { error } = await supabase
      .from("lead_dismissals")
      .upsert(
        { business_id: businessId, lead_id: leadId },
        { onConflict: "business_id,lead_id" }
      );
    if (error) {
      setDismissedLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
      showToast(`Failed to dismiss lead: ${error.message}`, "error");
      return;
    }
    showToast("Lead dismissed", "info", {
      action: { label: "Undo", onClick: () => undismissLead(leadId) },
    });
  }

  return {
    requests,
    leadsLoading,
    setRequests,
    submitLead,
    createOnsiteQuoteLead,
    updateRequestStatus,
    saveQuote,
    refreshMainAppData,
    handleLeadUnlocked,
    loadLeadDetails,
    bookJob,
    dismissLead,
    undismissLead,
    dismissedLeadIds,
    updateLeadStage,
    archiveLead,
    unarchiveLead,
    updateLeadContactDetails,
  };
}
